import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PlusIcon,
  EyeIcon,
  MailIcon,
  PhoneIcon,
  FileTextIcon,
  ClipboardListIcon,
  BriefcaseIcon,
  DollarSignIcon,
  TrashIcon,
  ChevronRightIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { logActivity } from '../utils/activityLogger'
import { ColumnSelector } from '../components/ColumnSelector'
import { ListFilterSortBar } from '../components/ListFilterSortBar'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { useColumnSelector } from '../hooks/useColumnSelector'
import {
  EMPTY_DATE_RANGE,
  matchesDateRange,
  matchesMultiSelect,
  sortItems,
  uniqueFilterOptions,
  type DateRange,
  type SortDir,
} from '../lib/listFilterSort'

export function Customers() {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [cityFilter, setCityFilter] = useState<string[]>([])
  const [stateFilter, setStateFilter] = useState<string[]>([])
  const [createdRange, setCreatedRange] = useState<DateRange>(EMPTY_DATE_RANGE)
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [showAddForm, setShowAddForm] = useState(false)
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  })

  useEffect(() => {
    fetchCustomers()
  }, [])

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setCustomers(data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateCustomerStatus = async (customerId: string, newStatus: 'onboarding' | 'active' | 'terminated') => {
    try {
      const { error } = await supabase
        .from('customers')
        .update({ status: newStatus })
        .eq('id', customerId)

      if (error) throw error

      // Log activity
      await logActivity({
        activity_type: 'status_changed',
        entity_type: 'customer',
        entity_id: customerId,
        description: `Customer status changed to ${newStatus}`,
        metadata: { status: newStatus },
      })

      // Refresh the list
      fetchCustomers()
    } catch (error) {
      console.error('Error updating customer status:', error)
      alert('Error updating customer status: ' + (error as any).message)
    }
  }

  const handleViewCustomer = (customer: any) => {
    navigate(`/customers/view/${customer.id}`)
  }

  const handleCreateProposal = (customer: any) => {
    navigate(`/proposals?customerId=${customer.id}`)
  }

  const handleDeleteCustomer = async (customer: any) => {
    const label = customer.company_name || 'this customer'
    if (
      !window.confirm(
        `Are you sure you want to delete "${label}"? This will also delete their properties. Jobs, contracts, and invoices linked to this customer may block deletion. This action cannot be undone.`
      )
    ) {
      return
    }

    try {
      const { error } = await supabase.from('customers').delete().eq('id', customer.id)
      if (error) throw error
      setCustomers((prev) => prev.filter((c) => c.id !== customer.id))
    } catch (error: any) {
      console.error('Error deleting customer:', error)
      const msg = error?.message || ''
      if (msg.includes('foreign key') || msg.includes('referenced')) {
        alert(
          'Cannot delete this customer because it is linked to jobs, contracts, proposals, or invoices. Remove those records first.'
        )
      } else {
        alert('Failed to delete customer: ' + (msg || 'Unknown error'))
      }
    }
  }

  const handleAddCustomer = (e: React.FormEvent) => {
    e.preventDefault()
    const customer = {
      id: customers.length + 1,
      ...newCustomer,
      properties: 0,
      jobs: 0,
      status: 'active',
    }
    setCustomers([...customers, customer])
    setNewCustomer({ name: '', email: '', phone: '', address: '' })
    setShowAddForm(false)
  }

  const CUSTOMER_COLUMNS = [
    { id: 'customer', label: 'Customer' },
    { id: 'contact', label: 'Contact' },
    { id: 'status', label: 'Status' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(CUSTOMER_COLUMNS, { storageKey: 'customers' })

  const customerStatus = (customer: any) =>
    customer.status || (customer.is_active ? 'active' : 'terminated')

  const cityOptions = uniqueFilterOptions(customers.map((c) => c.city))
  const stateOptions = uniqueFilterOptions(customers.map((c) => c.state))

  const filteredCustomers = sortItems(
    customers.filter((customer) => {
      const status = customerStatus(customer)
      const matchesSearch =
        customer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.toLowerCase().includes(searchTerm.toLowerCase())
      return (
        matchesSearch &&
        matchesMultiSelect(status, statusFilter) &&
        matchesMultiSelect(customer.city, cityFilter) &&
        matchesMultiSelect(customer.state, stateFilter) &&
        matchesDateRange(customer.created_at, createdRange)
      )
    }),
    (customer) => {
      switch (sortBy) {
        case 'status':
          return customerStatus(customer)
        case 'email':
          return customer.email || ''
        case 'created':
          return customer.created_at || ''
        default:
          return customer.company_name || ''
      }
    },
    sortDir
  )

  return (
    <MobilePage>
      <MobilePageHeader
        title="Customers"
        subtitle={<span className="hidden sm:inline">Manage your customer accounts and information</span>}
        actions={
          <button
            onClick={() => navigate('/customers/create')}
            className={mobile.primaryBtn}
          >
            <PlusIcon className="w-5 h-5" />
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Customer</span>
          </button>
        }
      />

        {/* Stats Cards — desktop / tablet */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{customers.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
                <PlusIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Customers</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">
                  {customers.filter((c) => c.status === 'active' || (!c.status && c.is_active)).length}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-green-50 text-green-600">
                <EyeIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Properties</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">-</p>
              </div>
              <div className="p-3 rounded-lg bg-violet-50 text-violet-600">
                <EyeIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Jobs</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">-</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-50 text-orange-600">
                <EyeIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

        <ListFilterSortBar
          search={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search customers..."
          className="lg:p-6"
          filters={[
            {
              type: 'multi',
              label: 'Status',
              value: statusFilter,
              onChange: setStatusFilter,
              options: [
                { value: 'active', label: 'Active' },
                { value: 'onboarding', label: 'Onboarding' },
                { value: 'terminated', label: 'Terminated' },
              ],
            },
            {
              type: 'multi',
              label: 'City',
              value: cityFilter,
              onChange: setCityFilter,
              options: cityOptions,
            },
            {
              type: 'multi',
              label: 'State',
              value: stateFilter,
              onChange: setStateFilter,
              options: stateOptions,
            },
            {
              type: 'daterange',
              label: 'Created',
              value: createdRange,
              onChange: setCreatedRange,
            },
          ]}
          sortValue={sortBy}
          onSortChange={setSortBy}
          sortOptions={[
            { value: 'name', label: 'Name' },
            { value: 'email', label: 'Email' },
            { value: 'status', label: 'Status' },
            { value: 'created', label: 'Created' },
          ]}
          sortDir={sortDir}
          onSortDirChange={setSortDir}
          trailing={
            <div className="hidden lg:block">
              <ColumnSelector
                columns={CUSTOMER_COLUMNS}
                visibleColumns={visibleColumns}
                onToggle={toggleColumn}
              />
            </div>
          }
        />

        {/* Add Customer Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-2">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add New Customer</h2>
            <form onSubmit={handleAddCustomer} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  required
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  required
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  required
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <button
                  type="submit"
                  className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Customer
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="min-h-[44px] px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Mobile cards */}
        <div className="native-mobile-list space-y-2">
          {filteredCustomers.map((customer) => {
            const status = customer.status || (customer.is_active ? 'active' : 'terminated')
            return (
              <div
                key={customer.id}
                className={`${mobile.surface} overflow-hidden`}
              >
                <button
                  type="button"
                  onClick={() => navigate(`/customers/view/${customer.id}`)}
                  className="w-full text-left p-4 active:bg-gray-50"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {customer.company_name || 'N/A'}
                        </h3>
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                            status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : status === 'onboarding'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {status}
                        </span>
                      </div>
                      {(customer.email || customer.phone) && (
                        <div className="mt-1.5 space-y-0.5 text-xs text-gray-600">
                          {customer.email && (
                            <div className="flex items-center gap-1.5 truncate">
                              <MailIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{customer.email}</span>
                            </div>
                          )}
                          {customer.phone && (
                            <div className="flex items-center gap-1.5 truncate">
                              <PhoneIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{customer.phone}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  </div>
                </button>
              </div>
            )
          })}
          {filteredCustomers.length === 0 && (
            <div className="text-center py-10 text-sm text-gray-500">No customers found</div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Customer List</h2>
          </div>
          <div className="w-full">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  {visibleColumns.has('customer') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                  )}
                  {visibleColumns.has('contact') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                  )}
                  {visibleColumns.has('status') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  {visibleColumns.has('actions') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    {visibleColumns.has('customer') && (
                    <td className="px-3 py-3 min-w-0">
                      <div>
                        <button
                          onClick={() => navigate(`/customers/view/${customer.id}`)}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline text-left"
                        >
                          {customer.company_name || 'N/A'}
                        </button>
                        <div className="text-sm text-gray-500 truncate">
                          {customer.address || ''}
                          {customer.city && `, ${customer.city}`}
                          {customer.state && `, ${customer.state}`}
                          {customer.zip_code && ` ${customer.zip_code}`}
                        </div>
                      </div>
                    </td>
                    )}
                    {visibleColumns.has('contact') && (
                    <td className="px-3 py-3 min-w-0">
                      <div className="flex items-center gap-2">
                        <MailIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900 truncate">{customer.email || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <PhoneIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900 truncate">{customer.phone || 'N/A'}</span>
                      </div>
                    </td>
                    )}
                    {visibleColumns.has('status') && (
                    <td className="px-3 py-3 min-w-0">
                      <select
                        value={customer.status || (customer.is_active ? 'active' : 'terminated')}
                        onChange={(e) => updateCustomerStatus(customer.id, e.target.value as 'onboarding' | 'active' | 'terminated')}
                        className={`text-xs font-semibold rounded-full px-3 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                          customer.status === 'active' || (!customer.status && customer.is_active)
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : customer.status === 'onboarding'
                            ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        <option value="onboarding">Onboarding</option>
                        <option value="active">Active</option>
                        <option value="terminated">Terminated</option>
                      </select>
                    </td>
                    )}
                    {visibleColumns.has('actions') && (
                    <td className="px-3 py-3 min-w-0">
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/proposals/create?customer=${customer.id}`)}
                          className="text-violet-600 hover:text-violet-900"
                          title="Create Proposal"
                        >
                          <FileTextIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/contracts/create?customer=${customer.id}`)}
                          className="text-green-600 hover:text-green-900"
                          title="Create Contract"
                        >
                          <ClipboardListIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/jobs/create?customer_id=${customer.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Create Job"
                        >
                          <BriefcaseIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/invoice-generation?customer=${customer.id}`)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Create Invoice"
                        >
                          <DollarSignIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCustomer(customer)}
                          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete Customer"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
    
    </MobilePage>

  )
}
